var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createElement, useCallback, useMemo, useState, } from 'react';
import { useEffectOnce } from 'react-use';
import { appActions } from 'app/app.actions';
import { analytics } from 'app/utils/segment';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { ContextMenu } from 'flow-builder/components/contextMenu/contextMenu';
import { getConnections } from 'flow-builder/utils/node/getConnections/getConnections';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { getArrangeWorker } from './getWorker';
const workerTrackingEvents = {
    align: analytics.flowContextAlignClicked,
    arrange: analytics.flowContextNodeArrangeClicked,
};
export function useContextMenu({ props, state, handlers, }) {
    const worker = useMemo(getArrangeWorker, []);
    const dispatch = useAppDispatch();
    const { nodeState: { relations }, nodeState, setNodeState, } = state;
    const { flow } = props;
    const { onDeleteSelection } = handlers.node;
    const [loadingAction, setLoadingAction] = useState(null);
    const [{ isVisible, position, nodeId }, setState] = useState({
        isVisible: false,
        position: [0, 0],
        nodeId: null,
    });
    const onContextMenuHide = useCallback(() => {
        setState({
            isVisible: false,
            position: [0, 0],
            nodeId: null,
        });
    }, []);
    const onContextMenuShow = useCallback((id, event) => {
        if (!event) {
            return;
        }
        event.stopPropagation();
        const originalEvent = event.data.originalEvent;
        setState({
            isVisible: true,
            position: [originalEvent.clientX, originalEvent.clientY],
            nodeId: id,
        });
    }, []);
    const createWorkerRequest = useCallback((type) => () => {
        if (!nodeId)
            return;
        workerTrackingEvents[type]({ flowType: flow.type });
        setLoadingAction(type);
        const message = {
            type,
            payload: [nodeState, nodeId],
        };
        worker.postMessage(message);
    }, [flow, nodeId, nodeState, worker]);
    const handleDelete = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        onContextMenuHide();
        analytics.flowContextDeleteClicked({ flowType: flow.type });
        const { isConfirmed } = yield dispatch(appActions.showModal({
            modal: {
                type: 'app:confirm',
                arg: {
                    title: 'Delete node?',
                    description: "Are you sure you want to delete this node? Please note this action can't be undone.",
                    confirm: {
                        label: 'Delete',
                    },
                },
            },
        }));
        if (!isConfirmed) {
            analytics.flowContextDeleteCancelClicked({ flowType: flow.type });
            return;
        }
        analytics.flowContextDeleteConfirmClicked({ flowType: flow.type });
        onDeleteSelection();
    }), [flow.type, onDeleteSelection, onContextMenuHide, dispatch]);
    useEffectOnce(() => {
        worker.addEventListener('message', (event) => {
            const { payload } = event.data;
            const [nodeState] = payload;
            // Ideally this would be a part of the worker.
            // However, workers are not able to accept functions / objects with functions,
            // therefore we can't pass `nodeAttributes` to it.
            nodeState.connections = getConnections(nodeState.relations);
            setNodeState(nodeState);
            setLoadingAction(null);
            onContextMenuHide();
        });
    });
    const selectedNodeRelation = relations[nodeId || ''];
    const contextMenu = useMemo(() => {
        var _a;
        return isVisible
            ? createElement(ContextMenu, {
                handlers: {
                    onAlign: createWorkerRequest('align'),
                    onArrange: createWorkerRequest('arrange'),
                    onDelete: handleDelete,
                },
                loading: {
                    isAlignLoading: loadingAction === 'align',
                    isArrangeLoading: loadingAction === 'arrange',
                },
                disabled: {
                    isAlignDisabled: !(selectedNodeRelation === null || selectedNodeRelation === void 0 ? void 0 : selectedNodeRelation.parentId),
                    isArrangeDisabled: !(selectedNodeRelation === null || selectedNodeRelation === void 0 ? void 0 : selectedNodeRelation.children.length),
                    isDeleteDisabled: ((_a = selectedNodeRelation === null || selectedNodeRelation === void 0 ? void 0 : selectedNodeRelation.nodeType) === null || _a === void 0 ? void 0 : _a.toLowerCase()) ===
                        FlowNodeTypes.INIT,
                },
                position,
                onHide: onContextMenuHide,
            })
            : null;
    }, [
        isVisible,
        createWorkerRequest,
        handleDelete,
        loadingAction,
        position,
        onContextMenuHide,
        selectedNodeRelation,
    ]);
    return {
        onContextMenuShow,
        onContextMenuHide,
        contextMenu,
    };
}
